import {
  HappyTailListResponse,
  HappyTailListResponseSimplified,
  PetSummary,
  SearchResult,
} from "./types.ts";
import { getYearsAndMonths, hasNoAlphanumericCharacters } from "./util.ts";
import noDogPhotoAvailable from "./assets/images/dog_placeholder.jpg";
import noCatPhotoAvailable from "./assets/images/cat_placeholder.jpg";
import noCrittersPhotoAvailable from "./assets/images/bunny_placeholder.jpg";

const PROXY_BASE_URL = "https://seahumane-adoption-service-proxy.fly.dev";

/**
 * action - e.g. HappyTailList
 * params - an object specifiying all query parameters (except API key, which is not needed)
 */
export async function makeRequest(action: string, params: any) {
  // const url = `${PROXY_BASE_URL}/${action}?${new URLSearchParams(params)}`;
  // const result = await fetch(url);
  // return result.json();
  const keyValuePairs: [string, string][] = [];
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      for (const v of value) {
        keyValuePairs.push([key, v.toString()]);
      }
      continue;
    }
    keyValuePairs.push([key, value.toString()]);
  }
  const searchParams = new URLSearchParams(keyValuePairs).toString();
  const url = `${PROXY_BASE_URL}/${action}?${searchParams}`;
  try {
    // DML David added try-catch because bad petId makes server return 500
    const result = await fetch(url);
    if (!result.ok) {
      throw new Error("Network response was not ok");
    }
    return result.json();
  } catch (error) {
    console.error("There has been a problem with your fetch operation:", error);
    return null;
  }
}

/**
 * Add some defaults here so that less params are required when calling fetchHappyTailList
 */
const HAPPY_TAIL_LIST_DEFAULT_PARAMS = {
  pSiteID: "0",
  pFeaturedPet: "Include",
};

/**
 * calls the HappyTailList api and returns it in a slightly nicer format
 */
export async function fetchHappyTailList(params: any) {
  const _params = { ...HAPPY_TAIL_LIST_DEFAULT_PARAMS, ...params };
  const result = (await makeRequest(
    "HappyTailList",
    _params,
  )) as HappyTailListResponse;
  return {
    items: result?.arrayOfAnyType?.anyType ?? [],
  } as HappyTailListResponseSimplified;
}

export async function fetchAdoptablePetDetails(
  petId: string | number,
): Promise<any> {
  const result = (await makeRequest("AdoptableDetails", {
    animalID: petId,
  })) as any;

  return result;
}

export async function fetchAdoptedPetDetails(petId: string | number) {
  const result = (await makeRequest("HappyTailDetails", {
    pAdoptionID: petId,
  })) as any;
  return result;
}

export const SPECIES_IDS = {
  DOG: 1,
  CAT: 2,
  RABBIT: 3,
  FURRY: 5,
} as const;

export const SMALL_PETS_IDS = [SPECIES_IDS.RABBIT, SPECIES_IDS.FURRY] as const;

export const STAGE_IDS = {
  AVAILABLE: "2",
  AVAILABLE_FOR_PRESELECTION: "45747",
} as const;
type StageID = (typeof STAGE_IDS)[keyof typeof STAGE_IDS];

export type AdoptablePetSearchParams = {
  // "speciesID" => $speciesID,
  speciesID: number;
  sex: string;
  ageGroup: string;
  location: string;
  site: "Adoptions";
  onhold: string;
  orderby: "AnimalID" | "Random";
  primaryBreed: "All";
  secondaryBreed: "All";
  specialNeeds: "A";
  noDogs: "A";
  noCats: "A";
  noKids: "A";
  stageID: StageID;
};

/**
 * Add some defaults here so that less params are required when calling fetchHappyTailList
 */
const ADOPTABLE_SEARCH_DEFAULT_PARAMS = {
  sex: "A",
  ageGroup: "All",
  location: "",
  site: "Adoptions",
  onhold: "A",
  orderby: "Random",
  primaryBreed: "All",
  secondaryBreed: "All",
  specialNeeds: "A",
  noDogs: "A",
  noCats: "A",
  noKids: "A",
  stageID: STAGE_IDS.AVAILABLE,
};

function adoptablePetSearchResultItemToPetSummary(
  responseItem: SearchResult,
): PetSummary {
  const item = responseItem.adoptableSearch;
  if (!responseItem.adoptableSearch) {
    return undefined;
  }
  return {
    ...responseItem.adoptableSearch,
    photoUrl: !hasNoAlphanumericCharacters(responseItem.adoptableSearch?.photo)
      ? responseItem.adoptableSearch?.photo
      : responseItem.adoptableSearch?.species === "dog"
        ? noDogPhotoAvailable
        : responseItem.adoptableSearch?.species === "cat"
          ? noCatPhotoAvailable
          : noCrittersPhotoAvailable,
    speciesLower: item.species,
    ageMonths: Number(item.age),
    ageFormatted: getYearsAndMonths(Number(responseItem.adoptableSearch?.age)),
    breed: `${responseItem.adoptableSearch?.primaryBreed}${
      responseItem.adoptableSearch?.secondaryBreed
        ? ` / ${responseItem.adoptableSearch?.secondaryBreed}`
        : ""
    }`,
  };
}

export async function searchAdoptablePetsInner(params: any) {
  const _params = { ...ADOPTABLE_SEARCH_DEFAULT_PARAMS, ...params };
  let results: SearchResult[] = [];
  if (Array.isArray(_params.speciesID)) {
    // make multiple requests and combine the results
    for (const stageID of Object.values(STAGE_IDS)) {
      for (const speciesID of _params.speciesID) {
        const params = { ..._params, stageID, speciesID };
        const res = await searchAdoptablePetsInner(params);
        results.push(...res);
      }
    }
  } else {
    const res = (await makeRequest("AdoptableSearch", _params)) as any;
    if (Array.isArray(res.arrayOfXmlNode.xmlNode)) {
      results = res.arrayOfXmlNode.xmlNode;
    }
  }
  return results;
}

export async function searchAdoptablePets(params: AdoptablePetSearchParams) {
  const results = await searchAdoptablePetsInner(params);
  const petSummaries = results
    .map(adoptablePetSearchResultItemToPetSummary)
    .filter((pet) => pet !== undefined);
  // default sort by name alphabetically
  petSummaries.sort((a, b) => a.name.localeCompare(b.name));
  return petSummaries as PetSummary[];
}
